import { PrintJob } from "@/types";
import { QueryClient, QueryKey } from "@tanstack/vue-query";
import debounce from "lodash/debounce";
import type { DebouncedFunc } from "lodash";

export const PRINT_JOBS_QUERY_KEY = ["printJobs"] as const;
const RELATION_FIELDS = ["order", "file", "orderItem", "printer", "scheme", "layout"] as const;

/** `null` is the unassigned list. A number is that printer's queue. */
export function scopeQueryKey(printerId: number | null): QueryKey {
  return printerId == null ? ["printJobs", "unassigned"] : ["printJobs", "printer", printerId];
}

function scopePrinterIdFromKey(key: QueryKey): number | null | undefined {
  if (key[1] === "unassigned") return null;
  if (key[1] === "printer" && typeof key[2] === "number") return key[2];
  return undefined;
}

function normalizePrinterId(value: unknown): number | null | undefined {
  if (value === undefined) return undefined;
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function printerIdOf(job: Partial<PrintJob> | null | undefined): number | null | undefined {
  if (!job || !Object.prototype.hasOwnProperty.call(job, "printerId")) return undefined;
  return normalizePrinterId(job.printerId);
}

function deepMergeRelation(existing: any, incoming: any): any {
  if (incoming == null) return existing;
  if (existing == null || typeof existing !== "object" || typeof incoming !== "object" || Array.isArray(incoming)) {
    return incoming;
  }
  const merged = { ...existing };
  for (const key of Object.keys(incoming)) {
    const next = incoming[key];
    if (next !== undefined) {
      merged[key] = deepMergeRelation(existing[key], next);
    }
  }
  return merged;
}

function mergePrintJob(existing: PrintJob, incoming: PrintJob): PrintJob {
  const merged: PrintJob = { ...existing };
  Object.keys(incoming).forEach((key) => {
    const value = (incoming as any)[key];
    if (value === undefined) return;
    if ((RELATION_FIELDS as readonly string[]).includes(key)) {
      (merged as any)[key] = deepMergeRelation((existing as any)[key], value);
    } else {
      (merged as any)[key] = value;
    }
  });
  return merged;
}

const invalidateScopeFns = new Map<string, DebouncedFunc<(queryClient: QueryClient, printerId: number | null) => void>>();

function scheduleScopeRefetch(queryClient: QueryClient, printerId: number | null) {
  const cacheKey = printerId == null ? "unassigned" : `printer:${printerId}`;
  let fn = invalidateScopeFns.get(cacheKey);
  if (!fn) {
    fn = debounce((qc: QueryClient, id: number | null) => {
      qc.invalidateQueries({ queryKey: scopeQueryKey(id) });
    }, 1500);
    invalidateScopeFns.set(cacheKey, fn);
  }
  fn(queryClient, printerId);
}

export function invalidatePrintJobScopes(queryClient: QueryClient, printerIds: Array<number | null | undefined>) {
  const seen = new Set<string>();
  for (const printerId of printerIds) {
    if (printerId === undefined) continue;
    const token = printerId == null ? "unassigned" : String(printerId);
    if (seen.has(token)) continue;
    seen.add(token);
    queryClient.invalidateQueries({ queryKey: scopeQueryKey(printerId) });
  }
}

function cachesForScope(queryClient: QueryClient, printerId: number | null) {
  return queryClient.getQueriesData<PrintJob[]>({ queryKey: scopeQueryKey(printerId) }).filter((entry): entry is [QueryKey, PrintJob[]] => Array.isArray(entry[1]));
}

/** Move jobs into the cache for their printerId, dropping them from every other slice. */
export function relocatePrintJobsInCache(queryClient: QueryClient, updates: PrintJob[]) {
  const byId = new Map(updates.filter((job) => job.id != null).map((job) => [job.id as number, job]));
  if (!byId.size) return;

  const caches = queryClient.getQueriesData<PrintJob[]>({ queryKey: PRINT_JOBS_QUERY_KEY });
  for (const [key, old] of caches) {
    if (!old) continue;
    const scopePrinterId = scopePrinterIdFromKey(key);
    if (scopePrinterId === undefined) continue;

    let changed = false;
    const next: PrintJob[] = [];
    for (const job of old) {
      const update = job.id != null ? byId.get(job.id) : undefined;
      if (!update) {
        next.push(job);
        continue;
      }
      changed = true;
      const merged = { ...job, ...update };
      if ((merged.printerId ?? null) === scopePrinterId) next.push(merged);
    }
    for (const update of byId.values()) {
      if ((update.printerId ?? null) !== scopePrinterId) continue;
      if (next.some((job) => job.id === update.id)) continue;
      next.push(update);
      changed = true;
    }
    if (changed) queryClient.setQueryData(key, next);
  }
}

export function patchPrintJobInCache(queryClient: QueryClient, updatedPrintJob: PrintJob, action?: string) {
  if (!updatedPrintJob?.id) return;
  const jobId = updatedPrintJob.id;

  if (action === "removePrintJob" || (updatedPrintJob as any).archivedAt || (updatedPrintJob as any).endDate || (updatedPrintJob as any).abortDate) {
    queryClient.setQueriesData<PrintJob[]>({ queryKey: PRINT_JOBS_QUERY_KEY }, (old) => {
      if (!old?.some((job) => job.id === jobId)) return old;
      return old.filter((job) => job.id !== jobId);
    });
    return;
  }

  const caches = queryClient.getQueriesData<PrintJob[]>({ queryKey: PRINT_JOBS_QUERY_KEY });
  let found = false;
  const incomingPrinterId = printerIdOf(updatedPrintJob);

  for (const [key, old] of caches) {
    if (!old?.length) continue;
    const index = old.findIndex((job) => job.id === jobId);
    if (index === -1) continue;
    found = true;

    const scopePrinterId = scopePrinterIdFromKey(key);
    const merged = mergePrintJob(old[index], updatedPrintJob);
    const nextPrinterId = incomingPrinterId === undefined ? (old[index].printerId ?? null) : incomingPrinterId;

    if (scopePrinterId !== undefined && nextPrinterId !== scopePrinterId) {
      queryClient.setQueryData(
        key,
        old.filter((job) => job.id !== jobId),
      );
      const destination = cachesForScope(queryClient, nextPrinterId);
      for (const [destKey, destRows] of destination) {
        if (destRows.some((job) => job.id === jobId)) continue;
        queryClient.setQueryData(destKey, [...destRows, merged]);
      }
      scheduleScopeRefetch(queryClient, scopePrinterId);
      scheduleScopeRefetch(queryClient, nextPrinterId);
      continue;
    }

    const next = [...old];
    next[index] = merged;
    queryClient.setQueryData(key, next);
  }

  if (!found) {
    // A thin event with no printer id is not enough to choose a slice.
    if (incomingPrinterId === undefined) return;
    const destinationPrinterId = incomingPrinterId;
    for (const [destKey, destRows] of cachesForScope(queryClient, destinationPrinterId)) {
      if (destRows.some((job) => job.id === jobId)) continue;
      queryClient.setQueryData(destKey, [...destRows, updatedPrintJob]);
    }
    scheduleScopeRefetch(queryClient, destinationPrinterId);
  }
}
